// Data source entries — list via POST /data-source-entries/all (server-side
// pagination, scoped to a data source). Each entry is a "row": one value per
// column of the parent data source. The parent's column definitions come from
// the data source details endpoint (datasourceApi). Normalization stays
// isolated in toEntry so any drift is a one-file fix (mirrors datasourceApi).
//
// A row can be created, edited (PATCH :id, same body as create) and deleted in
// bulk (`{ entryIds: string[] }` — note the key differs from the data-source
// endpoint's `dataSourceIds`).
//
// Exporting these rows does NOT live here: the contract is keyed by the PARENT
// source (`{ dataSourceId, entryIds? }`, entryIds omitted = all), so it sits on
// datasourceApi as exportDataSource even though entries are what it emits.

import { baseApi } from '../baseApi';
import { ROUTES } from '../routes';

export interface DataSourceEntryListItem {
  id: string;
  /** Column value keyed by columnId. */
  values: Record<string, string>;
  createdAt: string;
}

export interface DataSourceEntriesPage {
  items: DataSourceEntryListItem[];
  total: number;
}

/** POST /data-source-entries/all body — the data source to list, plus paging. */
export interface DataSourceEntriesQueryArgs {
  dataSource: string;
  page: number;
  limit: number;
}

export interface CreateDataSourceEntryPayload {
  dataSource: string;
  entryValues: { columnId: string; keyValue: string }[];
}

/** PATCH data-source-entries/:id — the body is the create payload unchanged, so
 *  the one form serves both modes and only the id rides outside it. */
export interface UpdateDataSourceEntryArgs {
  id: string;
  body: CreateDataSourceEntryPayload;
}

/** POST data-source-entries/delete-multiple — one call for any number of rows.
 *  Note the key is `entryIds`, NOT the `dataSourceIds` its sibling on
 *  datasource/delete-multiple takes. */
export interface DeleteDataSourceEntriesPayload {
  entryIds: string[];
}

interface ApiEntryValue {
  columnId?: string | null;
  keyValue?: string | null;
}

interface ApiDataSourceEntry {
  _id: string;
  entryValues?: ApiEntryValue[] | null;
  createdAt?: string | null;
}

type ApiDataSourceEntriesPage = {
  items?: ApiDataSourceEntry[];
  totalRecords?: number;
};

const toEntry = (e: ApiDataSourceEntry): DataSourceEntryListItem => {
  const values: Record<string, string> = {};
  for (const value of e.entryValues ?? []) {
    if (value.columnId) values[value.columnId] = value.keyValue ?? '';
  }
  return { id: e._id, values, createdAt: e.createdAt ?? '' };
};

export const dataSourceEntriesApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getDataSourceEntries: build.query<DataSourceEntriesPage, DataSourceEntriesQueryArgs>({
      query: (body) => ({ ...ROUTES.dataSourceEntries.list, data: body }),
      transformResponse: (res: ApiDataSourceEntriesPage) => {
        const items = res?.items ?? [];
        return { items: items.map(toEntry), total: res?.totalRecords ?? items.length };
      },
      providesTags: [{ type: 'DataSourceEntries', id: 'LIST' }],
    }),
    createDataSourceEntry: build.mutation<{ _id?: string }, CreateDataSourceEntryPayload>({
      query: (body) => ({ ...ROUTES.dataSourceEntries.create, data: body }),
      // Must match what the list PROVIDES, or the new row never appears — the
      // create modal lives inside the already-mounted entries page.
      invalidatesTags: [{ type: 'DataSourceEntries', id: 'LIST' }],
    }),
    updateDataSourceEntry: build.mutation<{ _id?: string }, UpdateDataSourceEntryArgs>({
      query: ({ id, body }) => ({ ...ROUTES.dataSourceEntries.update(id), data: body }),
      invalidatesTags: [{ type: 'DataSourceEntries', id: 'LIST' }],
    }),
    deleteDataSourceEntries: build.mutation<unknown, DeleteDataSourceEntriesPayload>({
      query: (body) => ({ ...ROUTES.dataSourceEntries.deleteMultiple, data: body }),
      invalidatesTags: [{ type: 'DataSourceEntries', id: 'LIST' }],
    }),
  }),
});

export const {
  useGetDataSourceEntriesQuery,
  useCreateDataSourceEntryMutation,
  useUpdateDataSourceEntryMutation,
  useDeleteDataSourceEntriesMutation,
} = dataSourceEntriesApi;
